<template>
  <div class="pod-comment-item mt-2" v-bind:class="getPodCommentClasses">
    <div v-if="!showEditForm" class="px-4 py-2">
      <div class="d-flex justify-space-between">
        <div v-on:click="commentClick" class="" style="max-width: 44%;">
          <v-tooltip location="bottom" open-delay="200" :aria-label="getCreatedBy">
            <template v-slot:activator="{ props }">
              <h3 v-bind="props" class="text-truncate">{{ getCreatedBy }}</h3>
            </template>
            <span>{{ getCreatedBy }}</span>
          </v-tooltip>
        </div>

        <div v-if="comment.start !== null" v-on:click="commentClick" style="max-width: 37%;">
          <div v-if="!showCompact" class="pod-comment-inout-time" data-cy="commentItem-inOutTime">
            <div class="d-flex flex-row float-left mr-2">
              <div class="mr-1 text-uppercase">
                {{ $t('routes.pod.menuComment.inTime') }}
              </div>
              <div>
                {{ toTimeMilliseconds(comment.start) }}
              </div>
            </div>
            <div v-if="comment.end" class="d-flex flex-row float-left">
              <div class="mr-1 text-uppercase">
                {{ $t('routes.pod.menuComment.outTime') }}
              </div>
              <div>
                {{ toTimeMilliseconds(comment.end) }}
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex align-center" style="max-width: 19%;">
          <v-tooltip location="bottom" open-delay="200" z-index="1200" :aria-label="isoToDatetime(comment.createdat)">
            <template v-slot:activator="{ props }">
              <div v-bind="props" v-on:click="commentClick" class="text-center">
                {{ getTimeDifference }}
              </div>
            </template>
            <span>{{ isoToDatetime(comment.createdat) }}</span>
          </v-tooltip>

          <v-menu
            v-if="$canControlPodService()"
            v-model="showingContextMenu"
            :close-on-content-click="false"
            location="bottom"
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                variant="text"
                rounded="circle"
                width="28px"
                height="28px"
                :aria-label="$t('routes.pod.menuComment.commentActions')"
                icon
                data-cy="commentItem-actions-btn"
              >
                <v-icon size="small">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list width="180" density="comfortable" data-cy="commentItem-actions-list">
              <v-list-item @click="toggleReplyForm" data-cy="commentItem-actions-list-reply">
                <template v-slot:prepend>
                  <v-icon size="small">mdi-reply-outline</v-icon>
                </template>
                <v-list-item-title>{{ $t('routes.pod.menuComment.reply') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="showEdit" @click="toggleEditForm"
                           data-cy="commentItem-actions-list-edit">
                <template v-slot:prepend>
                  <v-icon size="small">mdi-pencil-outline</v-icon>
                </template>
                <v-list-item-title>{{ $t('routes.pod.menuComment.edit') }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                v-if="showDelete"
                ref="actionsListDelete"
                @click="showDeleteDialog"
                data-cy="commentItem-actions-list-delete"
              >
                <template v-slot:prepend>
                  <v-icon size="small">mdi-delete-outline</v-icon>
                </template>

                <v-list-item-title>{{ $t('routes.pod.menuComment.delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <div
        class="pod-comment-content"
        v-bind:class="commentContentClass"
        v-on:click="commentClick"
        v-on:dblclick="commentDblClick"
        data-cy="commentItem-content"
      >
        {{ comment.content }}
      </div>
    </div>

    <PodCommentForm
      v-if="showEditForm"
      v-bind:key="true"
      v-bind:editComment="comment"
      v-on:visibility="showEditForm = $event"
      class="">
    </PodCommentForm>

    <PodCommentForm
      v-if="showReplyForm"
      v-bind:key="false"
      v-bind:replyComment="comment"
      v-on:visibility="showReplyForm = $event"
      class="">
    </PodCommentForm>

    <div v-show="showAllReplys">
      <div v-for="comm in childComments" v-bind:key="comm.id" class="d-flex pl-4">
        <div class="pt-4">
          <v-icon color="primary">mdi-subdirectory-arrow-right</v-icon>
        </div>

        <PodCommentItem v-bind:comment="comm" v-bind:level="level+1" class="flex-grow-1"
                        data-cy="commentItem-replyForm">
        </PodCommentItem>
      </div>
    </div>

    <CommentDeleteDialog
      v-if="showDelete"
      v-model="showingDeleteDialog"
      :comment="comment"
    />

  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { usePlayerStore } from '@/stores/PlayerStore.js';
import { usePodCommentStore } from '@/stores/PodCommentStore.js';
import PodCommentForm from './PodCommentForm.vue';
import CommentDeleteDialog from './CommentDeleteDialog.vue';
import { toTimeMilliseconds } from '@/components/formatters.js';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { differenceInHours } from 'date-fns/differenceInHours';
import { differenceInYears } from 'date-fns/differenceInYears';
import { differenceInWeeks } from 'date-fns/differenceInWeeks';
import { differenceInDays } from 'date-fns/differenceInDays';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { isoToDatetime } from '@/components/formatters.js';

export default {
  name: 'PodCommentItem',
  components: {
    PodCommentForm,
    CommentDeleteDialog,
  },
  props: {
    comment: {
      type: Object,
      required: true,
    },
    level: {
      type: Number,
      required: true,
    },
    dataCy: {
      type: String,
    },
  },
  data: function () {
    return {
      selected: false,
      showAllReplys: true,
      showReplyForm: false,
      showEditForm: false,
      // inOutToggle: true,
      likeProcessing: false,
      checkProcessing: false,

      showingContextMenu: false,
      showingDeleteDialog: false,
    };
  },
  mounted: function () {
    this.selected = this.comment.checked;
  },
  methods: {
    isoToDatetime,
    toTimeMilliseconds,
    stub: function () {
      this.showingContextMenu = false;
    },
    showDeleteDialog: function () {
      this.showingContextMenu = false;
      this.showingDeleteDialog = true;
    },
    toggleReplyForm: function () {
      this.showReplyForm = !this.showReplyForm;
      this.showEditForm = false;

      if (this.showReplyForm) {
        this.closeOtherForms('reply');
      }

      this.showingContextMenu = false;
    },
    toggleEditForm: function () {
      this.showEditForm = !this.showEditForm;
      this.showReplyForm = false;

      if (this.showEditForm) {
        this.closeOtherForms('edit');
      }

      this.showingContextMenu = false;
    },
    // toggleInOut: function() {
    //   if (this.comment.end) {
    //     this.inOutToggle = !this.inOutToggle;
    //   }
    // },
    toggleShowReplys: function () {
      this.showAllReplys = !this.showAllReplys;
    },
    commentClick: function() {
      const playerStore = usePlayerStore();
      playerStore.setSeekTimeEvent(this.comment.start);
    },
    commentDblClick: function () {
      if (!this.showEdit) {
        return; // only the creator can edit the comment
      }

      this.toggleEditForm();
    },
    closeOtherForms: function (from) {
      const value = this.comment.id + from;
      const podCommentStore = usePodCommentStore();
      return podCommentStore.setCloseFormsEvent(value);
    },
    toggleLike: function () {
      if (this.likeProcessing || (!this.comment.likedBy || !Array.isArray(this.comment.likedBy))) {
        return;
      }

      // Prevent multiple clicks.
      this.likeProcessing = true;
      setTimeout(function () {
        this.likeProcessing = false;
      }.bind(this), 3000);

      const data = {
        podId: this.comment.podid,
        payload: {
          commentId: this.comment.id,
          userId: this.user.id,
        },
      };

      if (this.isLiked) {
        const podCommentStore = usePodCommentStore();
        podCommentStore.dislikeComment(data)
          .then(function () {
            console.log('Dislike');
          });
      } else {
        const podCommentStore = usePodCommentStore();
        podCommentStore.likeComment(data)
          .then(function () {
            console.log('Like');
          });
      }
    },
    toggleCheck: function () {
      if (this.checkProcessing) {
        return;
      }

      // Prevent multiple clicks.
      this.checkProcessing = true;
      setTimeout(function () {
        this.checkProcessing = false;
      }.bind(this), 3000);

      const data = {
        podId: this.comment.podid,
        payload: {
          commentId: this.comment.id,
          userId: this.user.id,
        },
      };

      const podCommentStore = usePodCommentStore();
      if (this.comment.checked) {
        podCommentStore.uncheckComment(data)
          .then(function () {
            this.selected = false;

            console.log('Uncheck');
          }.bind(this));
      } else {
        podCommentStore.checkComment(data)
          .then(function () {
            this.selected = true;

            console.log('Check');
          }.bind(this));
      }
    },
  },
  computed: {
    showEdit: function () {
      if (!this.comment.createdby) return false;
      const commentCreator = this.comment.createdby.id;
      return (this.user.id === commentCreator);
    },
    showDelete: function () {
      if (!this.comment.createdby) return false;
      const commentCreator = this.comment.createdby.id;
      return (this.user.id === commentCreator);
    },
    user: function() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    showCompact: function () {
      return !!this.comment.parentid;
    },
    childComments: function () {
      const podCommentStore = usePodCommentStore();
      return podCommentStore.getComments(this.comment.id);
    },
    playerCurrentTime: function() {
      const playerStore = usePlayerStore();
      return playerStore.currentTime;
    },
    commentContentClass: function () {
      return {
        // 'pod-comment-first-content': !this.showCompact,
        'pod-active-comment': !this.showCompact &&
          this.comment.start !== null &&
          (!this.showReplyForm && !this.showEditForm) &&
          (this.playerCurrentTime >= this.comment.start &&
            this.playerCurrentTime < Math.max(this.comment.end, this.comment.start + 1)),
      };
    },
    getPodCommentClasses() {
      return {
        'pod-comment-first-item': !this.showCompact,
        'pod-comment-active-header': this.showReplyForm || this.showEditForm,
      };
    },
    getTimeDifference: function () {
      if (!this.comment) {
        return '?';
      }

      const now = new Date();
      const commentCreatedAt = new Date(this.comment.createdat);

      let years = differenceInYears(now, commentCreatedAt);
      if (years >= 1) {
        return this.$t('common.time.years', years);
      }

      let months = differenceInMonths(now, commentCreatedAt);
      if (months >= 1) {
        return this.$t('common.time.months', months);
      }

      let weeks = differenceInWeeks(now, commentCreatedAt);
      if (weeks >= 1) {
        return this.$t('common.time.weeks', weeks);
      }

      let days = differenceInDays(now, commentCreatedAt);
      if (days >= 1) {
        return this.$t('common.time.days', days);
      }

      let hours = differenceInHours(now, commentCreatedAt);
      if (hours >= 1) {
        return this.$t('common.time.hours', hours);
      }

      return this.$t('common.time.minutes', differenceInMinutes(now, commentCreatedAt));
    },
    isLiked: function () {
      let result = false;

      if (this.comment.likedBy) {
        result = this.comment.likedBy.includes(this.user.id);
      }

      return result;
    },
    getCreatedBy: function () {
      if (this.comment.createdby) {
        return `${this.comment.createdby.firstname} ${this.comment.createdby.lastname}`;
      }

      if (this.comment.email) {
        return this.comment.email;
      }

      return this.$t('common.deletedUser');
    },
  },
};
</script>

<style>
.pod-comment-item {
  width: 100%;

}

.pod-comment-first-item {
  /* border-width: 0px 0px 2px 0px; */
  /* border-style: solid; */
  /* border-color: #000000; */
  border-radius: 10px;
  /* padding: 10px 14px; */
}

.v-theme--dark .pod-comment-first-item {
  background-color: #2f3640;
}

.v-theme--light .pod-comment-first-item {
  background-color: #e6ecf1;
}

.pod-comment-content {
  white-space: pre-line;
  word-break: break-word;
  /* background-color: #2E2E2E; */
}

/* .pod-comment-first-content {
  background-color: #2E2E2E;
} */

.pod-comment-active-header {
  border: 1px solid #3dabff;
  border-radius: 10px;

}

.pod-comment-active-header > div:first-child {
  color: #000000 !important;
  background-color: #3dabff !important;
  border-radius: 10px 10px 0px 0px;
}

.pod-comment-active-header > div:first-child i {
  color: #000000 !important;
}

.pod-comment-inout-time {
  /* cursor: pointer; */
  display: inline-block;
  background-color: #FFFFFF;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 5px;
  color: #000000;
}

.pod-comment-inout-time > div > div:nth-child(2n) {
  font-weight: 600;
}

/* .pod-comment-inout-time > div:first-child,
.pod-comment-inout-time > div:nth-child(4) {
  font-weight: 600;
  color: white;
} */

.pod-active-comment {
  color: #3dabff;
  /* color: #000000;  */
  /* background-color: #3dabff;  */
}

.pod-comment-checkbox i {
  font-size: 18px !important;
  margin-top: -2px;
}
</style>
